import { forwardRef } from 'react';
import FormInput from '../FormInput';
import type { ComponentPropsWithoutRef } from 'react';

// ============================================
// Geo Types - Internal Types do Backend
// ============================================

// Geo.Dot (latitude, longitude)
interface GeoDotProps {
    value?: { lat: number; lng: number };
    onChange?: (value: { lat: number; lng: number }) => void;
    disabled?: boolean;
    className?: string;
}

export const GeoDot = forwardRef<HTMLDivElement, GeoDotProps>(({ value = { lat: 0, lng: 0 }, onChange, disabled, className = '' }, ref) => {
    const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const lat = parseFloat(e.target.value) || 0;
        onChange?.({ lat, lng: value.lng });
    };

    const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const lng = parseFloat(e.target.value) || 0;
        onChange?.({ lat: value.lat, lng });
    };

    return (
        <div ref={ref} className={`flex gap-2 ${className}`}>
            <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                <FormInput type="number" step="0.000001" min="-90" max="90" value={value.lat} onChange={handleLatChange} disabled={disabled} placeholder="-23.550520" />
            </div>
            <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                <FormInput type="number" step="0.000001" min="-180" max="180" value={value.lng} onChange={handleLngChange} disabled={disabled} placeholder="-46.633308" />
            </div>
        </div>
    );
});
GeoDot.displayName = 'GeoDot';

// Geo.Line (array of coordinates)
interface GeoLineProps {
    value?: Array<{ lat: number; lng: number }>;
    onChange?: (value: Array<{ lat: number; lng: number }>) => void;
    disabled?: boolean;
    className?: string;
}

export const GeoLine = forwardRef<HTMLDivElement, GeoLineProps>(({ value = [], onChange, disabled, className = '' }, ref) => {
    const addPoint = () => {
        onChange?.([...value, { lat: 0, lng: 0 }]);
    };

    const removePoint = (index: number) => {
        onChange?.(value.filter((_, i) => i !== index));
    };

    const updatePoint = (index: number, updatedPoint: { lat: number; lng: number }) => {
        const newValue = [...value];
        newValue[index] = updatedPoint;
        onChange?.(newValue);
    };

    return (
        <div ref={ref} className={className}>
            <div className="space-y-3">
                {value.map((point, index) => (
                    <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1">
                            <GeoDot value={point} onChange={(newPoint) => updatePoint(index, newPoint)} disabled={disabled} />
                        </div>
                        {!disabled && (
                            <button type="button" onClick={() => removePoint(index)} className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md">
                                ×
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {!disabled && (
                <button type="button" onClick={addPoint} className="mt-3 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md">
                    + Adicionar ponto
                </button>
            )}
        </div>
    );
});
GeoLine.displayName = 'GeoLine';

// Geo.Polygon (two dots defining a polygon)
interface GeoPolygonProps {
    value?: { dotA: { lat: number; lng: number }; dotB: { lat: number; lng: number } };
    onChange?: (value: { dotA: { lat: number; lng: number }; dotB: { lat: number; lng: number } }) => void;
    disabled?: boolean;
    className?: string;
}

export const GeoPolygon = forwardRef<HTMLDivElement, GeoPolygonProps>(({ value = { dotA: { lat: 0, lng: 0 }, dotB: { lat: 0, lng: 0 } }, onChange, disabled, className = '' }, ref) => {
    const handleDotAChange = (dotA: { lat: number; lng: number }) => {
        onChange?.({ dotA, dotB: value.dotB });
    };

    const handleDotBChange = (dotB: { lat: number; lng: number }) => {
        onChange?.({ dotA: value.dotA, dotB });
    };

    return (
        <div ref={ref} className={className}>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ponto A (Noroeste)</label>
                    <GeoDot value={value.dotA} onChange={handleDotAChange} disabled={disabled} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ponto B (Sudeste)</label>
                    <GeoDot value={value.dotB} onChange={handleDotBChange} disabled={disabled} />
                </div>
            </div>
        </div>
    );
});
GeoPolygon.displayName = 'GeoPolygon';
